import React, { useEffect, useRef, useState } from "react";
import { Card, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  checkFilterCityCount,
  getBookmarks,
  searchBookmark,
} from "../../redux/slices/searchSlice";
import CityDetailLimitModal from "../restriction-modals/CityDetailLimitModal";
import "./SavedCitiesSection.scss";

// Initial page size + step. Auto-loads more 5 at a time via
// IntersectionObserver when the sentinel scrolls into view.
const PAGE_LIMIT = 12;

const PROFESSION_SHORT_LABELS = {
  "Home Health and Personal Care Aides": "HHA/PCA",
  "Licensed Practical and Licensed Vocational Nurses": "LPN/LVN",
  "Medical and Health Services Managers": "MHSM",
  "Nurse Anesthetists": "CRNA",
  "Nurse Midwives": "CNM",
  "Nurse Practitioners": "NP",
  "Nursing Assistants": "CNA",
  "Nursing Instructors and Teachers Postsecondary": "Nursing Faculty",
  "Registered Nurses": "RN",
};

const formatPriceShort = (price) => {
  if (!price) return null;
  const num = parseInt(price);
  if (num >= 1000000000) {
    return `$${(num / 1000000000).toFixed(1)}B`;
  }
  if (num >= 1000000) {
    return `$${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `$${(num / 1000).toFixed(1)}k`;
  }
  return `$${num}`;
};

/**
 * "Saved cities" — full-list management view on the Profile page.
 * Displays bookmarked cities from the getBookmarks API.
 * Clicking the row opens the city details page.
 */
const SavedCitiesSkeleton = () => (
  <div className="sc-row sc-skeleton-row" aria-hidden="true">
    <div className="sc-row-main">
      <div className="sc-row-header">
        <div className="sc-row-title sc-skeleton-title" />
        <div className="sc-row-actions">
          <div className="sc-skeleton-button" />
        </div>
      </div>
      <div className="sc-row-meta">
        <div className="sc-skeleton-tag sc-skeleton-role" />
        <div className="sc-skeleton-tag sc-skeleton-salary" />
        <div className="sc-skeleton-tag sc-skeleton-price" />
      </div>
    </div>
  </div>
);

const SavedCitiesSection = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isLoading = useSelector((s) => s.search?.isLoading);
  const profileStore = useSelector((s) => s.profile);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [allCities, setAllCities] = useState([]);
  const [total, setTotal] = useState(0);
  const [deletingId, setDeletingId] = useState(null);
  const [cityLimitModalVisible, setCityLimitModalVisible] = useState(false);
  const [upgradeSource, setUpgradeSource] = useState(null);

  // Fetch first page on mount
  useEffect(() => {
    dispatch(getBookmarks({ page: 1, limit: PAGE_LIMIT }))
      .unwrap()
      .then((response) => {
        // API response structure: { data: { total, data: [...] } }
        const paginationData = response?.data || {};
        const citiesData = paginationData?.data || [];
        const totalCount = paginationData?.total || 0;
        
        // Ensure citiesData is an array
        const citiesArray = Array.isArray(citiesData) ? citiesData : [];
        
        setAllCities(citiesArray);
        setTotal(totalCount);
        setPage(1);
        setHasMore(citiesArray.length < totalCount);
      })
      .catch((err) => {
        console.error("Failed to fetch bookmarks:", err);
        toast.error("Failed to load saved cities");
        setAllCities([]);
        setTotal(0);
      });
  }, [dispatch]);

  // Auto-load more rows when the sentinel at the bottom of the list
  // scrolls into view. Stops once hasMore is false.
  const sentinelRef = useRef(null);
  const listScrollRef = useRef(null);
  
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || isLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          const nextPage = page + 1;
          dispatch(getBookmarks({ page: nextPage, limit: PAGE_LIMIT }))
            .unwrap()
            .then((response) => {
              // API response structure: { data: { total, data: [...] } }
              const paginationData = response?.data || {};
              const newCities = paginationData?.data || [];
              const newCitiesArray = Array.isArray(newCities) ? newCities : [];
              
              setAllCities((prev) => [...prev, ...newCitiesArray]);
              setPage(nextPage);
              
              const totalLoaded = allCities.length + newCitiesArray.length;
              setHasMore(totalLoaded < (paginationData?.total || 0));
            })
            .catch((err) => {
              console.error("Failed to fetch more bookmarks:", err);
            });
        }
      },
      {
        root: listScrollRef.current || null,
        rootMargin: "200px",
      },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [dispatch, page, hasMore, isLoading, allCities.length]);

  const openCityDetails = async (city) => {
    const planSlug = profileStore?.profileItem?.data?.plan?.slug;
    if (planSlug === "free") {
      try {
        const result = await dispatch(
          checkFilterCityCount({
            plan_slug: planSlug,
            increment_count: true,
            city_id: city?.id,
          }),
        );
        const restrictionData = result?.payload?.checkFilterCityCountData;
        if (
          restrictionData?.is_comparisan === false &&
          restrictionData?.used_count >= restrictionData?.comparisanMaxCount &&
          restrictionData?.incrementSuccess === false
        ) {
          setUpgradeSource("city_detail_limit");
          setCityLimitModalVisible(true);
          return;
        }
      } catch (err) {
        console.error("Error checking city detail limit:", err);
      }
    }
    navigate(`/search/details/${city?.id}`, {
      state: { cityData: city },
    });
  };

  const handleDeleteCity = (city, e) => {
    e.stopPropagation();
    setDeletingId(city?.id);
    
    dispatch(searchBookmark({
      city: city?.city,
      state: city?.state,
      role: city?.role,
    }))
      .unwrap()
      .then(() => {
        toast.success("City removed from bookmarks");
        // Refresh the bookmarks list to get fresh data
        return dispatch(getBookmarks({ page: 1, limit: PAGE_LIMIT }))
          .unwrap();
      })
      .then((response) => {
        // Update the list with fresh data
        const paginationData = response?.data || {};
        const citiesData = paginationData?.data || [];
        const totalCount = paginationData?.total || 0;
        
        const citiesArray = Array.isArray(citiesData) ? citiesData : [];
        
        setAllCities(citiesArray);
        setTotal(totalCount);
        setPage(1);
        setHasMore(citiesArray.length < totalCount);
      })
      .catch((err) => {
        console.error("Failed to delete bookmark:", err);
        toast.error("Failed to remove city");
      })
      .finally(() => setDeletingId(null));
  };

  return (
    <React.Fragment>
    <CityDetailLimitModal
      isVisible={cityLimitModalVisible}
      onClose={() => setCityLimitModalVisible(false)}
      eventSource={upgradeSource}
    />
    <Card id="saved-cities" className="saved-cities-card mt-3" data-tour="pf-cities">
      <Card.Body>
        <div className="sc-head">
          <div className="sc-head-left">
            {/* Location icon */}
            <div className="sc-head-icon" aria-hidden="true">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            </div>
            <h5 className="sc-head-title">Saved cities</h5>
          </div>
          <span className="sc-head-count">
            {isLoading && allCities.length === 0 ? (
              "Loading…"
            ) : (
              <>
                <span className="sc-head-count-dot" aria-hidden="true" />
                {total || allCities.length} saved
              </>
            )}
          </span>
        </div>

        {isLoading && allCities.length === 0 && (
          <div className="sc-list">
            {Array.from({ length: 5 }).map((_, i) => (
              <SavedCitiesSkeleton key={i} />
            ))}
          </div>
        )}

        {!isLoading && allCities.length === 0 && (
          <div className="sc-empty">
            <div className="sc-empty-icon" aria-hidden="true">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            </div>
            <p className="sc-empty-text">
              No saved cities yet. Bookmark cities on the search page to add
              them here.
            </p>
          </div>
        )}

        {allCities.length > 0 && (
          <div className="sc-list" ref={listScrollRef}>
            {allCities.map((city, idx) => {
              const netMonthlySalaryShort = formatPriceShort(city?.net_monthly_salary);
              const homePriceShort = formatPriceShort(city?.home_prices);
              const roleShort = PROFESSION_SHORT_LABELS[city?.role] || city?.role || "N/A";
              return (
                <div
                  key={city?.id}
                  className="sc-row"
                  data-tour={idx === 0 ? "pf-city1" : undefined}
                  role="button"
                  tabIndex={0}
                  onClick={() => openCityDetails(city)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openCityDetails(city);
                    }
                  }}
                  aria-label={`Open ${city?.city || "saved city"} details`}
                >
                  <div className="sc-row-main">
                    <div className="sc-row-header">
                      <div className="sc-row-title">
                        {city?.city && city?.state ? `${city.city}, ${city.state}` : city?.city || "Unknown city"}
                      </div>
                      <div className="sc-row-actions">
                        <button
                          type="button"
                          className="sc-delete-btn"
                          data-tour={idx === 0 ? "pf-city-del" : undefined}
                          onClick={(e) => handleDeleteCity(city, e)}
                          disabled={deletingId === city?.id}
                          aria-label={`Delete ${city?.city || "saved city"}`}
                        >
                          {deletingId === city?.id ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                              <path d="M10 11v6" />
                              <path d="M14 11v6" />
                              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="sc-row-meta">
                      {roleShort && (
                        <span className="sc-role-tag">{roleShort}</span>
                      )}
                      {netMonthlySalaryShort && (
                        <span className="sc-salary-tag">
                          <span className="sc-salary-tag-label">Net Salary</span>
                          <span className="sc-salary-tag-value">{netMonthlySalaryShort}<span className="sc-salary-tag-suffix">/mo</span></span>
                        </span>
                      )}
                      {homePriceShort && (
                        <span className="sc-home-price-tag">
                          <span className="sc-home-price-tag-label">Typical Home Value for the Region</span>
                          <span className="sc-home-price-tag-value">{homePriceShort}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {/* Sentinel — when this scrolls into view IntersectionObserver
                fires the next page fetch. Also doubles as a "loading more"
                indicator while a fetch is in flight. */}
            {hasMore && (
              <div ref={sentinelRef} className="sc-load-more">
                {isLoading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <span>Loading more…</span>
                )}
              </div>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
    </React.Fragment>
  );
};

export default SavedCitiesSection;
